import { ref } from 'vue';
import {
  sendVerificationCode,
  verifyCode,
  resendVerificationCode,
} from '@monolith/legacy/services/api/email-validation';

interface EmailVerificationError {
  message?: string;
  success?: boolean;
}

export function useEmailValidation() {
  const isLoading = ref(false);
  const isResending = ref(false);
  const error = ref<string | null>(null);

  const sendCode = async (email: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      await sendVerificationCode(email);
      return true;
    } catch (err) {
      const response = err as EmailVerificationError;
      error.value = response.message || 'Failed to send verification code';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const verify = async (token: string, code: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      await verifyCode(token, code);
      return true;
    } catch (err) {
      const response = err as EmailVerificationError;
      error.value = response.message || 'Invalid verification code';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const resend = async (email: string): Promise<boolean> => {
    isResending.value = true;
    error.value = null;

    try {
      await resendVerificationCode(email);
      return true;
    } catch (err) {
      const response = err as EmailVerificationError;
      error.value = response.message || 'Failed to resend verification code';
      return false;
    } finally {
      isResending.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  return {
    isLoading,
    isResending,
    error,
    sendCode,
    verify,
    resend,
    clearError,
  };
}
